/**
 * useExport Hook
 * Custom hook for handling model export in React components
 */

import { useCallback, useState } from 'react';
import useEditorStore from '../stores/useEditorStore';
import { showExportDialog, quickExport } from '../utils/exporters/exportDialog';
import { EXPORT_FORMATS, EXPORT_CONFIG } from '../utils/exportConfig';

/**
 * Hook for exporting the current 3D model
 * @returns {Object} Export utilities and state
 *   - isExporting: boolean indicating if export is in progress
 *   - error: error message if export failed
 *   - handleExport: function to show export dialog
 *   - handleQuickExport: function to quick export with default settings
 */
export function useExport() {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);

  // Get the shaded mesh from editor store
  const sceneRefs = useEditorStore((state) => state.sceneRefs);
  const shadedMesh = sceneRefs?.shadedMesh;

  /**
   * Handle export with dialog
   */
  const handleExport = useCallback(async () => {
    if (!shadedMesh) {
      setError('No model loaded. Please load a model first.');
      return;
    }

    setIsExporting(true);
    setError(null);

    try {
      await showExportDialog(shadedMesh, {
        defaultFormat: EXPORT_CONFIG.defaultFormat,
        defaultFilename: 'socket'
      });
    } catch (err) {
      console.error('Export error:', err);
      setError(`Export failed: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  }, [shadedMesh]);

  /**
   * Handle quick export with default format
   */
  const handleQuickExport = useCallback((format = EXPORT_CONFIG.defaultFormat) => {
    if (!shadedMesh) {
      setError('No model loaded. Please load a model first.');
      return;
    }

    setIsExporting(true);
    setError(null);

    try {
      quickExport(shadedMesh, format);
    } catch (err) {
      console.error('Quick export error:', err);
      setError(`Export failed: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  }, [shadedMesh]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isExporting,
    error,
    clearError,
    handleExport,
    handleQuickExport,
    shadedMesh,
    supportedFormats: EXPORT_FORMATS
  };
}
